import { useEffect, useRef, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { MessagesSquare, Send, Loader2 } from "lucide-react";
import { formatDateTime } from "@/lib/status";

const ROLE_LABEL = {
  admin: "Yönetici",
  purchasing: "Satınalma",
  warehouse: "Depo",
  user: "Personel",
};
const ROLE_BADGE = {
  admin: "bg-slate-200 text-slate-700",
  purchasing: "bg-indigo-100 text-indigo-700",
  warehouse: "bg-emerald-100 text-emerald-700",
  user: "bg-amber-100 text-amber-700",
};

export default function RequestChat({ request, onSent }) {
  const { user } = useAuth();
  const messages = request.messages || [];
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const scrollRef = useRef(null);

  // Auto-scroll to the newest message.
  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages.length]);

  const send = async () => {
    const body = text.trim();
    if (!body || sending) return;
    setSending(true);
    try {
      await api.post(`/requests/${request.id}/messages`, { text: body });
      setText("");
      onSent && onSent();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Mesaj gönderilemedi");
    } finally {
      setSending(false);
    }
  };

  const onKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  return (
    <div data-testid="request-chat" className="bg-white border border-slate-200 rounded-lg p-5">
      <div className="flex items-center gap-2 mb-3">
        <MessagesSquare className="h-4 w-4 text-indigo-600" />
        <div className="text-xs font-bold uppercase tracking-wider text-slate-600">
          Mesajlaşma {messages.length > 0 && <span className="text-slate-400">({messages.length})</span>}
        </div>
      </div>

      <div
        ref={scrollRef}
        data-testid="chat-messages"
        className="space-y-3 max-h-80 overflow-y-auto thin-scroll mb-3 pr-1"
      >
        {messages.length === 0 ? (
          <div data-testid="chat-empty" className="text-sm text-slate-400 text-center py-6">
            Henüz mesaj yok. Talep sahibi ve satınalma arasında ilk mesajı siz yazın.
          </div>
        ) : (
          messages.map((m, idx) => {
            const mine = m.user_id === user?.id;
            return (
              <div key={m.id || idx} className={`flex flex-col ${mine ? "items-end" : "items-start"}`}>
                <div className="flex items-center gap-1.5 mb-0.5 px-1">
                  <span className="text-xs font-semibold text-slate-700">{m.user_name}</span>
                  <span className={`text-[10px] font-bold uppercase px-1.5 py-0.5 rounded-full ${ROLE_BADGE[m.role] || "bg-slate-100 text-slate-600"}`}>
                    {ROLE_LABEL[m.role] || m.role}
                  </span>
                </div>
                <div
                  data-testid={`chat-message-${idx}`}
                  className={`max-w-[80%] text-sm leading-snug rounded-2xl px-3.5 py-2 whitespace-pre-wrap break-words ${
                    mine
                      ? "bg-indigo-600 text-white rounded-br-sm"
                      : "bg-slate-100 text-slate-900 rounded-bl-sm"
                  }`}
                >
                  {m.text}
                </div>
                <span className="text-[10px] text-slate-400 mt-0.5 px-1">{formatDateTime(m.created_at)}</span>
              </div>
            );
          })
        )}
      </div>

      <div className="flex items-end gap-2 border-t border-slate-100 pt-3">
        <Textarea
          data-testid="chat-input"
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={onKeyDown}
          placeholder="Mesaj yazın… (Enter ile gönder)"
          rows={1}
          className="min-h-[44px] max-h-32 resize-none flex-1"
        />
        <Button
          data-testid="chat-send-btn"
          onClick={send}
          disabled={!text.trim() || sending}
          className="h-11 w-11 shrink-0 p-0 bg-indigo-600 hover:bg-indigo-700 text-white"
          aria-label="Gönder"
        >
          {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
        </Button>
      </div>
    </div>
  );
}
